import { Fragment } from 'react';
import Link from 'next/link';

import {
  Breadcrumb as BreadcrumbRoot,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { breadcrumbClearBgStyle } from '@/components/Breadcrumb/breadcrumb-clear-bg.style';
import { cn } from '@/lib/utils';

const listTextClass =
  'font-sans font-semibold text-[11px] leading-[16.5px] tracking-[0.5px] uppercase text-[#F4F4F4] items-center';

export type BreadcrumbItemData = {
  label: string;
  href?: string;
};

export type BreadcrumbProps = {
  items: BreadcrumbItemData[];
  className?: string;
  variant?: 'dark' | 'light';
  /**
   * Optional navigate interceptor (Next Link onNavigate). Call preventDefault
   * to keep listing soft-nav / filter state in sync instead of a bare SPA hop.
   */
  onItemNavigate?: (href: string, event: { preventDefault: () => void }) => void;
};

const variantClasses = {
  dark: {
    link: 'text-[#F4F4F4] hover:text-[#A8EA3A]',
    page: 'text-[#F4F4F4]',
    separator: 'text-[#A8EA3A]',
  },
  light: {
    link: 'text-[#898989] hover:text-foreground dark:hover:text-white',
    page: 'text-foreground dark:text-white',
    separator: 'text-[#898989]',
  },
} as const;

export function Breadcrumb({
  items,
  className,
  variant = 'dark',
  onItemNavigate,
}: BreadcrumbProps) {
  if (items.length === 0) {
    return null;
  }

  const colors = variantClasses[variant];

  return (
    <BreadcrumbRoot className={cn('bg-transparent', className)} style={breadcrumbClearBgStyle}>
      <BreadcrumbList className={cn(listTextClass, 'list-none gap-1.5 bg-transparent sm:gap-2.5')}>
        {items.map((item, index) => (
          <Fragment key={`${index}-${item.href ?? 'current'}-${item.label}`}>
            {index > 0 ? (
              <BreadcrumbSeparator
                className={cn('bg-transparent px-1 [&>svg]:hidden', colors.separator)}
              >
                /
              </BreadcrumbSeparator>
            ) : null}
            <BreadcrumbItem className="bg-transparent">
              {item.href ? (
                <BreadcrumbLink
                  asChild
                  className={cn(
                    'bg-transparent shadow-none ring-0 transition-colors focus-visible:ring-0',
                    colors.link,
                  )}
                >
                  <Link
                    href={item.href}
                    onNavigate={
                      onItemNavigate
                        ? (event) => {
                            onItemNavigate(item.href!, {
                              preventDefault: () => event.preventDefault(),
                            });
                          }
                        : undefined
                    }
                  >
                    {item.label}
                  </Link>
                </BreadcrumbLink>
              ) : (
                <BreadcrumbPage
                  className={cn('bg-transparent font-semibold shadow-none', colors.page)}
                >
                  {item.label}
                </BreadcrumbPage>
              )}
            </BreadcrumbItem>
          </Fragment>
        ))}
      </BreadcrumbList>
    </BreadcrumbRoot>
  );
}
